import { env } from '@/config/env';
import {
  GET_RETRY_BASE_MS,
  GET_RETRY_JITTER,
  GET_RETRY_MAX_ATTEMPTS,
  GET_RETRY_MAX_MS,
  REQUEST_TIMEOUT_MS,
} from '@/constants/http';
import { record } from '@/helpers/serverTime';
import { logger } from '@/lib/logger';
import { jitter } from '@/utils/jitter';

import { createAppError, isAbortedError, isAppError, toAppError } from './errors';
import { createRequestId } from './requestId';
import { linkSignals } from './signals';
import { metaSchema, type ResponseMeta } from './types';
import { unwrap } from './unwrap';

import type * as z from 'zod/mini';

export type JsonMethod = 'GET' | 'POST';

export type RequestJsonInit = { method: 'GET' } | { method: 'POST'; body: unknown };

function acceptLanguage(): string {
  const lang = document.documentElement.lang;
  return lang === '' ? 'en' : lang;
}

function jsonHeaders(method: JsonMethod, requestId: string): Headers {
  const headers = new Headers();
  headers.set('Accept', 'application/json');
  headers.set('Accept-Language', acceptLanguage());
  headers.set('X-Request-Id', requestId);
  if (method === 'POST') headers.set('Content-Type', 'application/json');
  return headers;
}

function uploadHeaders(requestId: string): Headers {
  const headers = new Headers();
  headers.set('Accept', 'application/json');
  headers.set('Accept-Language', acceptLanguage());
  headers.set('X-Request-Id', requestId);
  // Never set Content-Type: only the browser knows the multipart boundary.
  return headers;
}

function recordMetaTimestamp(body: unknown): void {
  if (typeof body !== 'object' || body === null) return;
  if (!('meta' in body) || typeof body.meta !== 'object' || body.meta === null) return;
  const meta = body.meta;
  if (!('timestamp' in meta) || typeof meta.timestamp !== 'string') return;
  record(meta.timestamp);
}

async function parseJsonSafely(res: Response): Promise<unknown> {
  const text = await res.text();
  try {
    return JSON.parse(text) as unknown;
  } catch (cause) {
    throw createAppError({
      kind: 'parse',
      code: 'PARSE_ERROR',
      status: res.status,
      cause,
    });
  }
}

function throwIfOffline(): void {
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    throw createAppError({ kind: 'offline', code: 'OFFLINE' });
  }
}

async function sleep(ms: number, signal: AbortSignal): Promise<void> {
  if (ms <= 0) return;
  await new Promise<void>((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const timer = setTimeout(resolve, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(new DOMException('Aborted', 'AbortError'));
    };
    signal.addEventListener('abort', onAbort, { once: true });
  });
}

function shouldRetryGet(error: unknown, attempt: number, signal: AbortSignal): boolean {
  if (signal.aborted) return false;
  if (attempt + 1 >= GET_RETRY_MAX_ATTEMPTS) return false;
  if (!isAppError(error)) return false;
  if (error.kind === 'network' || error.kind === 'timeout' || error.kind === 'offline') {
    return true;
  }
  return error.status !== undefined && error.status >= 500;
}

/**
 * Typed to accept GET only. Retrying a mutation is a financial bug; the type makes it inexpressible.
 */
async function retryGet<T>(run: () => Promise<T>, signal: AbortSignal): Promise<T> {
  let attempt = 0;
  for (;;) {
    try {
      return await run();
    } catch (error) {
      if (isAbortedError(error)) throw error;
      if (!shouldRetryGet(error, attempt, signal)) throw error;
      const backoff = Math.min(GET_RETRY_BASE_MS * 2 ** attempt, GET_RETRY_MAX_MS);
      await sleep(jitter(backoff, GET_RETRY_JITTER), signal);
      attempt += 1;
    }
  }
}

async function requestJsonOnce<T>(
  path: string,
  init: RequestJsonInit,
  schema: z.ZodMiniType<T>,
  signal: AbortSignal,
  onMeta?: (meta: ResponseMeta) => void,
): Promise<T> {
  throwIfOffline();

  const linked = linkSignals(signal, REQUEST_TIMEOUT_MS);
  const requestId = createRequestId();
  const url = `${env.apiBaseUrl}${path}`;

  logger.debug('[API] →', { url, method: init.method, requestId });

  try {
    const res = await fetch(url, {
      method: init.method,
      headers: jsonHeaders(init.method, requestId),
      body: init.method === 'POST' ? JSON.stringify(init.body) : null,
      credentials: 'omit',
      signal: linked.signal,
    });

    const body: unknown = await parseJsonSafely(res);
    recordMetaTimestamp(body);
    if (onMeta !== undefined && typeof body === 'object' && body !== null && 'meta' in body) {
      const parsedMeta = metaSchema.safeParse(body.meta);
      if (parsedMeta.success) onMeta(parsedMeta.data);
    }
    return unwrap(body, schema, { status: res.status });
  } catch (error) {
    if (linked.didTimeout()) {
      throw createAppError({ kind: 'timeout', code: 'TIMEOUT', requestId });
    }
    if (isAbortedError(error)) throw error;
    if (isAppError(error)) throw error;
    throw toAppError(error);
  } finally {
    linked.cleanup();
  }
}

export async function requestJson<T>(
  path: string,
  init: RequestJsonInit,
  schema: z.ZodMiniType<T>,
  signal: AbortSignal,
  onMeta?: (meta: ResponseMeta) => void,
): Promise<T> {
  if (init.method === 'GET') {
    return retryGet(() => requestJsonOnce(path, init, schema, signal, onMeta), signal);
  }
  return requestJsonOnce(path, init, schema, signal, onMeta);
}

export async function uploadWithProgress<T>(
  path: string,
  formData: FormData,
  schema: z.ZodMiniType<T>,
  onProgress: (percent: number | null) => void,
  signal: AbortSignal,
): Promise<T> {
  throwIfOffline();

  const linked = linkSignals(signal, REQUEST_TIMEOUT_MS);
  const requestId = createRequestId();
  const url = `${env.apiBaseUrl}${path}`;

  logger.debug('[API] →', { url, method: 'POST', requestId, upload: true });

  try {
    // Abort before constructing XHR: `xhr.abort()` before `send()` is a no-op in the browser.
    if (linked.signal.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }

    const { status, body } = await new Promise<{ status: number; body: unknown }>(
      (resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', url);
        xhr.withCredentials = false;
        xhr.timeout = REQUEST_TIMEOUT_MS;
        xhr.responseType = 'text';

        uploadHeaders(requestId).forEach((value, key) => {
          xhr.setRequestHeader(key, value);
        });

        xhr.upload.onprogress = (event) => {
          onProgress(
            event.lengthComputable && event.total > 0
              ? Math.round((event.loaded / event.total) * 100)
              : null,
          );
        };

        const onAbort = () => {
          xhr.abort();
        };
        if (linked.signal.aborted) {
          onAbort();
        } else {
          linked.signal.addEventListener('abort', onAbort, { once: true });
        }

        xhr.onload = () => {
          linked.signal.removeEventListener('abort', onAbort);
          try {
            resolve({ status: xhr.status, body: JSON.parse(xhr.responseText) as unknown });
          } catch (cause) {
            reject(
              createAppError({
                kind: 'parse',
                code: 'PARSE_ERROR',
                status: xhr.status,
                requestId,
                cause,
              }),
            );
          }
        };

        xhr.onerror = () => {
          linked.signal.removeEventListener('abort', onAbort);
          reject(createAppError({ kind: 'network', code: 'NETWORK_ERROR', requestId }));
        };

        xhr.ontimeout = () => {
          linked.signal.removeEventListener('abort', onAbort);
          reject(createAppError({ kind: 'timeout', code: 'TIMEOUT', requestId }));
        };

        xhr.onabort = () => {
          linked.signal.removeEventListener('abort', onAbort);
          reject(new DOMException('Aborted', 'AbortError'));
        };

        xhr.send(formData);
      },
    );

    recordMetaTimestamp(body);
    return unwrap(body, schema, { status });
  } catch (error) {
    if (linked.didTimeout()) {
      throw createAppError({ kind: 'timeout', code: 'TIMEOUT', requestId });
    }
    if (isAbortedError(error)) throw error;
    if (isAppError(error)) throw error;
    throw toAppError(error);
  } finally {
    linked.cleanup();
  }
}
